'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { motion } from 'motion/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Eye, EyeOff, Mail, Lock, AlertCircle, ChevronRight, Shield, UserRound } from 'lucide-react';
import { authClient } from '@/lib/auth/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const ease = [0.22, 1, 0.36, 1] as const;

const DEMO_ACCOUNTS = [
  {
    name: 'Alex Morgan',
    role: 'Admin',
    email: 'admin@salescrm.demo',
    password: 'Admin@Demo2026!',
    badge: 'Admin',
    badgeClass: 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400',
    avatarClass: 'bg-violet-100 text-violet-600 dark:bg-violet-500/20',
    Icon: Shield,
  },
  {
    name: 'Sarah Chen',
    role: 'Sales Rep',
    email: 'sarah.chen@salescrm.demo',
    password: 'Rep@Demo2026!',
    badge: 'Sales Rep',
    badgeClass: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
    avatarClass: 'bg-blue-100 text-blue-600 dark:bg-blue-500/20',
    Icon: UserRound,
  },
] as const;

export function LoginForm() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [demoLoadingIdx, setDemoLoadingIdx] = useState<number | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const signIn = async (emailVal: string, passwordVal: string) => {
    const { data, error: authError } = await authClient.signIn.email({
      email: emailVal,
      password: passwordVal,
    });

    if (authError) {
      if (authError.code === 'USER_BANNED' || authError.status === 403) {
        setError('Your account has been deactivated. Contact your administrator.');
      } else if (authError.status === 0 || authError.code === 'NETWORK_ERROR') {
        toast.error('Unable to connect. Check your internet connection and try again.');
      } else {
        setError('Invalid email or password. Please try again.');
      }
      return false;
    }

    if (data) {
      const role = (data.user as { role?: string } & typeof data.user)?.role;
      router.push(role === 'admin' ? '/admin/dashboard' : '/leads');
      return true;
    }
    return false;
  };

  const handleDemoLogin = async (idx: number) => {
    const account = DEMO_ACCOUNTS[idx];
    setDemoLoadingIdx(idx);
    setError(null);
    try {
      await signIn(account.email, account.password);
    } catch {
      toast.error('Unable to connect. Check your internet connection and try again.');
    } finally {
      setDemoLoadingIdx(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await signIn(email, password);
    } catch {
      toast.error('Unable to connect. Check your internet connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease }}
      className="w-full"
    >
      {/* Mobile logo */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.5, ease }}
        className="lg:hidden mb-10"
      >
        <h1 className="font-bold text-2xl">SalesCRM</h1>
      </motion.div>

      {/* Heading */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.6, ease }}
        className="mb-6"
      >
        <h2 className="font-display italic text-[2rem] lg:text-[2.25rem] text-foreground leading-tight">
          Welcome back
        </h2>
        <p className="mt-1.5 text-muted-foreground text-[15px]">
          Sign in to your SalesCRM account
        </p>
      </motion.div>

      {/* ── Demo accounts ─────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.6, ease }}
        className="mb-6"
      >
        <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-3">
          Quick demo access
        </p>

        <div className="flex flex-col gap-2.5">
          {DEMO_ACCOUNTS.map((account, idx) => {
            const isThisLoading = demoLoadingIdx === idx;
            const isAnyLoading = demoLoadingIdx !== null || isLoading;
            const { Icon } = account;

            return (
              <button
                key={account.email}
                type="button"
                disabled={isAnyLoading}
                onClick={() => handleDemoLogin(idx)}
                className={cn(
                  'group relative flex items-center gap-3.5 rounded-xl border bg-card px-4 py-3.5 text-left transition-all duration-150',
                  'hover:border-primary/30 hover:bg-primary/[0.02] hover:shadow-sm',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isAnyLoading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                  isThisLoading && 'border-primary/30 bg-primary/[0.03]',
                )}
              >
                {/* Avatar */}
                <div className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-bold transition-transform duration-150 group-hover:scale-105',
                  account.avatarClass,
                )}>
                  <Icon className="h-5 w-5" />
                </div>

                {/* Info */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[13px] font-semibold text-foreground leading-none">
                      {account.name}
                    </span>
                    <span className={cn(
                      'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold leading-none',
                      account.badgeClass,
                    )}>
                      {account.badge}
                    </span>
                  </div>
                  <p className="text-[12px] text-muted-foreground truncate leading-none mt-1">
                    {account.email}
                  </p>
                </div>

                {/* Chevron / Spinner */}
                <div className="shrink-0 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors">
                  {isThisLoading ? (
                    <span className="flex h-4 w-4 items-center justify-center">
                      <span className="h-3.5 w-3.5 rounded-full border-2 border-muted-foreground/30 border-t-primary animate-spin" />
                    </span>
                  ) : (
                    <ChevronRight className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-0.5" />
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <p className="mt-2.5 text-center text-[11px] text-muted-foreground/45">
          Click any card to sign in instantly (demo mode)
        </p>
      </motion.div>

      {/* Divider */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        className="relative mb-6"
      >
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-background px-3 text-muted-foreground/50 font-medium">
            or sign in with email
          </span>
        </div>
      </motion.div>

      {/* Error banner */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-5 flex items-start gap-3 rounded-lg border border-destructive/20 bg-destructive/8 px-4 py-3"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <p className="text-[13px] text-destructive leading-snug">{error}</p>
        </motion.div>
      )}

      {/* Form */}
      <motion.form
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45, duration: 0.6, ease }}
        onSubmit={handleSubmit}
        className="space-y-5"
      >
        {/* Email */}
        <div className="space-y-2">
          <Label
            htmlFor="email"
            className="text-xs font-sans font-medium tracking-wider uppercase text-muted-foreground"
          >
            Email address
          </Label>
          <div className="relative">
            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[15px] w-[15px] text-muted-foreground/50 pointer-events-none" />
            <Input
              id="email"
              type="email"
              placeholder="you@salescrm.com"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11 pl-10 rounded-lg bg-card text-sm placeholder:text-muted-foreground/40"
            />
          </div>
        </div>

        {/* Password */}
        <div className="space-y-2">
          <Label
            htmlFor="password"
            className="text-xs font-sans font-medium tracking-wider uppercase text-muted-foreground"
          >
            Password
          </Label>
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[15px] w-[15px] text-muted-foreground/50 pointer-events-none" />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-11 pl-10 pr-10 rounded-lg bg-card text-sm placeholder:text-muted-foreground/40"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/40 hover:text-muted-foreground transition-colors"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {/* Remember / Forgot */}
        <div className="flex items-center justify-between pt-0.5">
          <div className="flex items-center gap-2">
            <Checkbox id="remember" />
            <Label
              htmlFor="remember"
              className="text-sm font-normal text-muted-foreground cursor-pointer select-none"
            >
              Remember me
            </Label>
          </div>
          <a
            href="/reset-password"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Forgot password?
          </a>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={isLoading || demoLoadingIdx !== null}
          className="w-full h-11 rounded-lg text-sm font-sans font-medium tracking-wide"
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <span className="h-4 w-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              Signing in...
            </span>
          ) : (
            'Sign in'
          )}
        </Button>
      </motion.form>

      {/* Footer */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7, duration: 0.5 }}
        className="mt-8 text-center text-xs text-muted-foreground/50 leading-relaxed"
      >
        This is an invitation-only platform.
        <br />
        Contact your administrator for access.
      </motion.p>
    </motion.div>
  );
}
